// Supabase connection settings and the browser client.
//
// Configure with two public values (Supabase dashboard → Project Settings → API), in a
// `.env.local` file next to package.json (see .env.example and docs/SUPABASE_SETUP.md):
//   VITE_SUPABASE_URL=https://<project-ref>.supabase.co
//   VITE_SUPABASE_PUBLISHABLE_KEY=sb_publishable_...   (or the legacy VITE_SUPABASE_ANON_KEY=eyJ...)
// Both are safe to ship in the website: every table is protected by Row Level Security
// (supabase/migrations/0001_routezero_accounts.sql). NEVER put the service_role / secret key here.
//
// Without these values the app runs on the development mock sign-in (src/auth/mockAuthProvider.js).
import { createClient } from '@supabase/supabase-js';

const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
export const SUPABASE_URL = String(env.VITE_SUPABASE_URL || '').trim();
export const SUPABASE_KEY = String(env.VITE_SUPABASE_PUBLISHABLE_KEY || env.VITE_SUPABASE_ANON_KEY || '').trim();
export const supabaseConfigured = () => /^https:\/\/[^/]+/.test(SUPABASE_URL) && SUPABASE_KEY.length > 20;

// "Keep me signed in": supabase-js keeps its tokens in localStorage only when the learner
// ticked the box (survives closing the browser); otherwise in sessionStorage (ends with the
// browser session). PKCE code verifiers always go to localStorage, because the email links
// (confirm address, reset password) often open in a new tab.
export const REMEMBER_KEY = 'nell:v1:remember';
const safe = (name) => { try { const s = globalThis[name]; const t = '__rz_probe__'; s.setItem(t, '1'); s.removeItem(t); return s; } catch { return null; } };
export function setRemember(on) { const ls = safe('localStorage'); try { ls?.setItem(REMEMBER_KEY, on ? '1' : '0'); } catch { /* ignore */ } }
export function createRememberStorage(local = safe('localStorage'), session = safe('sessionStorage')) {
  const ses = session || local;
  const pick = (k) => (k.endsWith('-code-verifier') || local?.getItem(REMEMBER_KEY) === '1' ? local : ses);
  return {
    getItem: (k) => ses?.getItem(k) ?? local?.getItem(k) ?? null,
    setItem: (k, v) => { const to = pick(k); to?.setItem(k, v); for (const s of [local, ses]) if (s && s !== to) s.removeItem(k); },
    removeItem: (k) => { local?.removeItem(k); ses?.removeItem(k); },
  };
}

export const STORAGE_KEY = 'rz-auth';
/** A RouteZero-configured Supabase client (tests pass their own fetch + storage). */
export function createRzSupabaseClient({ url = SUPABASE_URL, key = SUPABASE_KEY, fetchImpl, storage = createRememberStorage() } = {}) {
  return createClient(url, key, {
    auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storage, storageKey: STORAGE_KEY },
    ...(fetchImpl ? { global: { fetch: fetchImpl } } : {}),
  });
}
let client = null;
/** The app's one Supabase client (null when not configured). */
export function getSupabase() {
  if (!client && supabaseConfigured()) client = createRzSupabaseClient();
  return client;
}
